import React, {useCallback, useMemo, useRef, useState} from "react";
import InventoryAdjustmentReportSearchSection from "../../../../../../components/search-sections/inventory-adjustment-report-search-section";
import {Tab, Tabs} from "../../../../../../components/base/Tabs";
import {dateComparator, exportCSVFile, formatMoney} from "../../../../../../../core/services/utils/utils";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {Paper, SvgIcon, useTheme} from "@mui/material";
import {Col, Row} from "reactstrap";
import BarChart from "../../../../../../components/base/chart/bar-chart";
import ToggleButtons from "../../../../../../components/base/button/toggle_buttons";
import Button from "../../../../../../components/base/button/button";
import {ReactComponent as Excel} from "../../../../../../../assets/images/excel.svg";
import Api from "../../../../../../../core/services/api/api";
import DateUtils from "../../../../../../../core/services/date-utils";
import ToastService from "../../../../../../../core/services/toast";
import useAccessLevels from "../../../../../../hooks/use-access-levels";


const InventoryAdjustmentReportTabs = {
    quantity: 'quantity',
    cost: 'cost'
}

const InventoryAdjustmentReportView = ({loading: loadingProp, product}) => {
    const [creatingReport, setCreatingReport] = useState(false);
    const [selectedTab, setSelectedTab] = useState(InventoryAdjustmentReportTabs.quantity);
    const [records, setRecords] = useState([]);
    const isMounted = useIsMounted();
    const counterRef = useRef(1);
    const operatorAccessLevels = useAccessLevels();
    const canExportInventoryAdjustmentReports = operatorAccessLevels.packages.bizInventory.exportInventoryAdjustmentReports;

    const tabs = React.useMemo(() => Object.values(InventoryAdjustmentReportTabs).map(e => ({
        value: e,
        name: (function () {
            switch (e) {
                case InventoryAdjustmentReportTabs.quantity:
                    return 'Quantity';
                case InventoryAdjustmentReportTabs.cost:
                    return 'Cost';
                default:
                    return "";
            }
        })(),
    })), [])

    /**
     * Memo version of the indicator for checking if the view is loading or not
     * @type {unknown}
     */
    const loading = useMemo(() => loadingProp || creatingReport, [loadingProp, creatingReport])

    /**
     * Adds new data to the [records] in the state
     * @param report
     * @return {Promise<void | Record<string, string>>}
     */
    const handleAddingReport = async (report) => {
        setCreatingReport(true);
        if (records.map(e => e.reportName).includes(report.reportName)) {
            setCreatingReport(false);
            return {
                'reportName': 'Report name cannot be duplicate',
            }
        }
        const forApi = {
            partIds: product?.id ? [product?.id] : report.parts?.map(e => e.id) ?? [],
            parentCategoryIds: product?.categoryId ? [product?.categoryId] : report?.category?.map(e => e.id),
            reasons: report?.reason?.map(e => e.id),
            startDateTime: report?.startDate,
            endDateTime: report?.endDate,
        }
        const response = await Api.getInventoryAdjustmentReport(forApi);
        if (!isMounted())
            return;
        setCreatingReport(false);
        if (response?.resultFlag) {
            if (!response.data.length) {
                ToastService.warning({title: "No Data Found", message: 'No data found for the selected filters to create a report from.'});
                return {};
            }
            setRecords(prevState => [
                ...prevState,
                {
                    id: counterRef.current++,
                    reportName: report.reportName,
                    records: response.data.map(record => ({
                        id: record.id,
                        submittedDate: record.submittedDateTime,
                        costChange: (record.isAdd ? 1 : -1) * record.cost,
                        quantityChange: record.oldQty + (record.isAdd ? 1 : -1) * record.qty,
                        categoryId: Number(record.partCategoryId),
                        categoryName: record.partCategory,
                        exportEntity: record,
                    }))
                }
            ])
        }
    }

    /**
     * Handles changes in the tab bar and sets the [selectedTab] with the new value
     */
    const onTabChanged = (e, newValue) => {
        setSelectedTab(newValue);
    }

    /**
     * Clears the added reports by setting [records] in the inner state to an empty array
     */
    const clearRecords = () => {
        setRecords([]);
    }

    /**
     * Exports the added reports in the state to csv file
     */
    const exportToCSV = () => {
        const exportTitle = `Inventory Adjustment Report`;
        const toBeExported = records?.map(e => e.records).flat()?.map((record, index) => {
            return {
                '': index + 1,
                "Id": record.exportEntity.id,
                "Type": record.exportEntity.isAdd ? "Add" : "Remove",
                "Quantity": record.exportEntity.qty,
                "Old Quantity": record.exportEntity.oldQty,
                "Average Cost": record.exportEntity.avgCost,
                "Cost": record.exportEntity.cost,
                "Category": record.exportEntity.partCategory,
                "part Number": record.exportEntity.partNumber,
                "Submitted Date Time": record.exportEntity.submittedDateTime ? DateUtils.serverToLocal(record.exportEntity.submittedDateTime).format(DateUtils.dateFormat) : '--',
            }
        });
        if (!records?.length) {
            ToastService.warning({message: "No records found for exporting!"});
            return;
        }
        exportCSVFile(toBeExported, exportTitle);
    }

    return (
        <div className={'inventory-adjustment-report-view'}>
            <div className={'d-flex flex-row justify-content-between align-items-center fast-connection-view w-100 px-2 mt-3 mb-4'}>
                {
                    !product &&
                    <p className={'view-title'}>
                        System Inventory Adjustment Reports
                    </p>
                }
            </div>
            <InventoryAdjustmentReportSearchSection
                loading={loading}
                singleProduct={!!product}
                handleAddingReport={handleAddingReport}
            />
            <div className={'mb-3 w-100'}/>
            {
                ((records?.length ?? 0) > 0) &&
                <>
                    <div className={'d-flex w-100 justify-content-between'}>
                        <div>
                            <Tabs value={selectedTab} onChange={onTabChanged}>
                                {
                                    tabs?.map((tab) => (
                                        <Tab key={tab.value} value={tab.value} label={tab.name}/>
                                    ))
                                }
                            </Tabs>
                        </div>
                        <div className={'d-flex'}>
                            <Button
                                className={'ml-3 d-flex justify-content-center align-items-center'}
                                variant={'outlined'}
                                color={'primary'}
                                onClick={clearRecords}
                            >
                                Clear
                            </Button>
                            {
                                canExportInventoryAdjustmentReports &&
                                <Button
                                    className={'ml-3 d-flex justify-content-center align-items-center'}
                                    variant={'outlined'}
                                    color={'primary'}
                                    onClick={exportToCSV}
                                >
                                    <SvgIcon className='icon normal' component={Excel}/>
                                    <p className='ml-2'>Export to Excel</p>
                                </Button>
                            }
                        </div>
                    </div>
                    <Paper className={'paper mx-2 mt-4 px-3 d-flex justify-content-center'}>
                        <ChartSection
                            dataset={records}
                            loading={loading}
                            single={!!product}
                            isQuantity={selectedTab === InventoryAdjustmentReportTabs.quantity}
                        />
                    </Paper>
                </>
            }
        </div>
    );
}

/**
 * Types of available visibilities for the chart
 */
const chartVisibilityTypes = {
    categoryBase: 'categoryBase',
    yearly: 'yearly',
    monthly: 'monthly',
    daily: 'daily',
}
/**
 * Chart Component shown int the login logs
 * @param dataset
 * @param loading
 * @param valueAggregator
 * @return {JSX.Element}
 * @constructor
 */
const ChartSection = ({dataset, loading, single, isQuantity}) => {
    const theme = useTheme();
    const chartColors = useMemo(() => Object.values(theme.palette['app-specific'].chart), [theme.palette])
    const [visibility, setVisibility] = useState(chartVisibilityTypes.daily);

    const visibilities = useMemo(() => [
        ...(!single ? [{
            value: chartVisibilityTypes.categoryBase,
            name: "Category Based",
            disabled: loading
        }] : []),

        {
            value: chartVisibilityTypes.yearly,
            name: "Yearly",
            disabled: loading
        },
        {
            value: chartVisibilityTypes.monthly,
            name: "Monthly",
            disabled: loading
        },
        {
            value: chartVisibilityTypes.daily,
            name: "Daily",
            disabled: loading
        },
    ], [loading, single])
    const rawDataSet = useRef({});

    /**
     * Gets the string representation of the day of the week of a date
     * @param {DateUtilsTypes.Instance} date
     * @return {string}
     */
    const dayOfWeek = useCallback((date) => {
        const dow = date.day();
        switch (dow) {
            case 0:
            default:
                return "Sunday";
            case 1:
                return "Monday";
            case 2:
                return "Tuesday";
            case 3:
                return "Wednesday";
            case 4:
                return "Thursday";
            case 5:
                return "Friday";
            case 6:
                return "Saturday";

        }
    }, [])

    /**
     * Converts day string to number
     * @param day
     * @return {number}
     */
    const dayToNumber = useCallback((day) => {
        switch (day) {
            case "Sunday":
            default:
                return 0;
            case "Monday":
                return 1;
            case "Tuesday":
                return 2;
            case "Wednesday":
                return 3;
            case "Thursday":
                return 4;
            case "Friday":
                return 5;
            case "Saturday":
                return 6;
        }
    }, [])

    /**
     * Gets the data set ready for the chart by aggregating them based on the selected visibility type
     * @param data
     * @return {object}
     */
    const getDatasetData = useCallback((data = []) => {
        const aggregated = {};
        switch (visibility) {
            case  chartVisibilityTypes.categoryBase: {
                data
                    ?.forEach(record => {
                        const category = record.categoryName;
                        aggregated[category] = {
                            cost: (!!aggregated[category] ? aggregated[category].cost += record.costChange : record.costChange),
                            quantity: (!!aggregated[category] ? aggregated[category].quantity += record.quantityChange : record.quantityChange),
                        };
                    })
                break;
            }
            case  chartVisibilityTypes.daily: {
                data
                    ?.sort((a, b) => DateUtils.serverToLocal(a.submittedDate).day() - DateUtils.serverToLocal(b.submittedDate).day())
                    ?.forEach(record => {
                        const dow = dayOfWeek(DateUtils.serverToLocal(record.submittedDate));
                        aggregated[dow] = {
                            cost: (!!aggregated[dow] ? aggregated[dow].cost += record.costChange : record.costChange),
                            quantity: (!!aggregated[dow] ? aggregated[dow].quantity += record.quantityChange : record.quantityChange),
                        };
                    })
                break;
            }
            case  chartVisibilityTypes.monthly: {
                data
                    ?.sort((a, b) => dateComparator(a.submittedDate, b.submittedDate))
                    ?.forEach(record => {
                        const date = DateUtils.serverToLocal(record.submittedDate).format('MMM YYYY');
                        aggregated[date] = {
                            cost: (!!aggregated[date] ? aggregated[date].cost += record.costChange : record.costChange),
                            quantity: (!!aggregated[date] ? aggregated[date].quantity += record.quantityChange : record.quantityChange),
                        };
                    })
                break;
            }
            case  chartVisibilityTypes.yearly: {
                data
                    ?.sort((a, b) => dateComparator(a.submittedDate, b.submittedDate))
                    ?.forEach(record => {
                        const date = DateUtils.serverToLocal(record.submittedDate).format('YYYY');
                        aggregated[date] = {
                            cost: (!!aggregated[date] ? aggregated[date].cost += record.costChange : record.costChange),
                            quantity: (!!aggregated[date] ? aggregated[date].quantity += record.quantityChange : record.quantityChange),
                        };
                    })
                break;
            }
            default:
                break;
        }
        return aggregated;
    }, [dayOfWeek, visibility])

    /**
     * Creates the tool tip value based on the percentage range of the bar
     * @type {(function(number[], any))}
     */
    const createTooTipValue = useCallback((value, tooltipItem) => {
        const reportName = tooltipItem.dataset.label;
        const rawData = rawDataSet.current[reportName];
        if (!rawData)
            return value;
        const data = rawData[tooltipItem.label];
        return `Qty: ${data.quantity} | Cost: ${formatMoney(data.cost)}`
    }, [])

    /**
     * Memo version of chart data passed to the bar-chart
     * @type {{datasets: *[], labels: *[]}}
     */
    const chartData = useMemo(() => {
        const chartData = {
            datasets: [],
            labels: [],
        }
        dataset.forEach((e, index) => {
            const allData = getDatasetData(e.records);
            rawDataSet.current[e.reportName] = allData;

            const data = isQuantity
                ? Object.fromEntries(Object.entries(allData).map(([key, value]) => [key, value.quantity]))
                : Object.fromEntries(Object.entries(allData).map(([key, value]) => [key, value.cost]))

            chartData.datasets.push({
                data: data,
                label: e.reportName,
                backgroundColor: chartColors[index % chartColors.length],
            })
            const _labels = [...new Set([...chartData.labels, ...Object.keys(data)])];
            chartData.labels = visibility === chartVisibilityTypes.daily
                ? _labels.sort((a, b) => dayToNumber(a) - dayToNumber(b))
                : visibility === chartVisibilityTypes.categoryBase
                    ? _labels
                    : visibility === chartVisibilityTypes.yearly
                        ? _labels.sort((a, b) => Number(a) - Number(b))
                        : _labels.sort((a, b) => DateUtils.serverToLocal(a).isBefore(DateUtils.serverToLocal(b), 'month')
                            ? -1
                            : DateUtils.serverToLocal(a).isAfter(DateUtils.serverToLocal(b), 'month')
                                ? 1
                                : 0
                        );
        })
        return chartData;
    }, [dataset, getDatasetData, isQuantity, chartColors, visibility, dayToNumber]);

    return (
        <Row className={'d-flex w-100 mb-4'}>
            <Col xs={12}
                 className={'my-4 transition mx-auto bar-chart'}>
                <BarChart
                    height={window.innerHeight / 2}
                    data={chartData}
                    createToolTipValue={createTooTipValue}
                    scales={{
                        scales: {
                            x: {},
                            y: {
                                ticks: {
                                    callback: (value) => !isQuantity ? formatMoney(value) : value,
                                }
                            },
                        }
                    }}
                />
            </Col>
            <Col xs={12} className={'d-flex justify-content-center mb-4'}>
                <ToggleButtons
                    size={'medium'}
                    value={visibility}
                    onChange={(_, v) => setVisibility(v)}
                    options={visibilities}
                />
            </Col>
        </Row>
    );

}

export default InventoryAdjustmentReportView;
